import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  LayoutGrid,
  Settings2,
  Clock,
  Plus,
  RefreshCw,
  UtensilsCrossed,
  ChefHat,
  AlertCircle,
} from 'lucide-react';
import apiService from '../services/api';
import PosCaisseSharedHeader from '../components/caisse/PosCaisseSharedHeader';
import PosOperationalAlertsBanner from '../components/caisse/PosOperationalAlertsBanner';
import { usePosDisplayTimeZone } from '../hooks/usePosDisplayTimeZone';
import { createCaisseIdempotencyKey } from '../utils/caisseIdempotency';
import RestaurantSplitModal from '../components/restaurant/RestaurantSplitModal';
import RestaurantAdditionDetailModal from '../components/restaurant/RestaurantAdditionDetailModal';
import RestaurantFloorPlanCanvas from '../components/restaurant/RestaurantFloorPlanCanvas';
import RestaurantPrepConfigPanel from '../components/restaurant/RestaurantPrepConfigPanel';
import RestaurantReleaseTableModal from '../components/restaurant/RestaurantReleaseTableModal';
import { POS_Z_CLASS } from '../utils/posZLayers';
import {
  formatRestaurantTablePanelTitle,
  formatRestaurantTableOptionLabel,
} from '../utils/restaurantTableLabel';
import { canPosAction, getAdminUserFromStorage } from '../utils/posStaffAccess';
import { buildCaisseAccueilHref } from '../utils/posCaisseHubNav';
import {
  applySizePreset,
  normalizePlanShape,
  suggestedDimensionsForCapacity,
} from '../utils/restaurantFloorPlan';
import {
  formatPrepNothingToSendToast,
  formatPrepSendSuccessToast,
} from '../utils/restaurantPrepSendFeedback';

const POLL_MS = 12_000;

function AdditionRow({
  a,
  sessionOk,
  moduleKitchenOk,
  processing,
  onView,
  onResume,
  onSendKitchen,
  onSplit,
}) {
  const cancelled = a.isCancelled || a.statut === 'ANNULEE';
  return (
    <li
      className={`border rounded-xl p-3 flex justify-between items-center gap-2 ${
        cancelled ? 'opacity-70 bg-slate-50 border-slate-200' : ''
      }`}
    >
      <div>
        <span className="font-mono text-sm">{a.numeroTicket || a.numero || a.id.slice(-6)}</span>
        {cancelled && (
          <span className="ml-2 text-xs font-medium text-rose-700 uppercase">Annulée</span>
        )}
        <span className="block text-sm text-slate-600">
          {a.statutPaiement} · total {Number(a.total).toFixed(2)} €
          {a.montantEncaisse != null && (
            <> · encaissé {Number(a.montantEncaisse).toFixed(2)} €</>
          )}
          {a.solde != null && Number(a.solde) > 0 && (
            <> · solde {Number(a.solde).toFixed(2)} €</>
          )}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          className="min-h-[44px] px-3 rounded-xl border border-slate-300 text-slate-900 text-sm"
          data-testid={`restaurant-view-addition-${a.id}`}
          onClick={() => onView(a.id)}
        >
          Voir la commande
        </button>
        {!cancelled && (
          <>
            <button
              type="button"
              className="min-h-[44px] px-3 rounded-xl bg-slate-900 text-white text-sm"
              data-testid={`restaurant-resume-addition-${a.id}`}
              onClick={() => onResume(a.id)}
              disabled={!sessionOk}
            >
              Reprendre
            </button>
            {moduleKitchenOk && (
              <button
                type="button"
                className="min-h-[44px] px-3 rounded-xl border border-orange-300 text-orange-900 text-sm"
                data-testid={`restaurant-send-kitchen-${a.id}`}
                disabled={!sessionOk || processing}
                onClick={() => onSendKitchen(a)}
              >
                Envoyer en préparation
              </button>
            )}
            {Number(a.montantEncaisse || 0) < 0.01 &&
              a.statutPaiement === 'EN_ATTENTE' &&
              Number(a.solde ?? a.total) > 0 && (
                <button
                  type="button"
                  className="min-h-[44px] px-3 rounded-xl border border-emerald-300 text-emerald-900 text-sm"
                  onClick={() => onSplit(a)}
                  disabled={!sessionOk}
                  data-testid={`restaurant-split-${a.id}`}
                >
                  Partager
                </button>
              )}
          </>
        )}
      </div>
    </li>
  );
}

function resolveCommercantId(searchParams) {
  const fromUrl = searchParams.get('commercantId');
  if (fromUrl) return fromUrl;
  try {
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    return user.commercantId || user.commercant?.id || null;
  } catch {
    return null;
  }
}

function formatElapsed(openedAt) {
  if (!openedAt) return '—';
  const ms = Date.now() - new Date(openedAt).getTime();
  if (ms < 0) return '0 min';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return `${h} h ${min % 60} min`;
}

function describeCloseBlocker(b) {
  const ref = b.numeroTicket || b.numero || b.commandeId?.slice(-6) || '—';
  if (b.reason === 'PARTIAL_PAYMENT') {
    return `Addition ${ref} : paiement partiel (solde ${Number(b.solde).toFixed(2)} €)`;
  }
  if (b.reason === 'UNPAID_BALANCE') {
    return `Addition ${ref} : solde dû ${Number(b.solde).toFixed(2)} €`;
  }
  return `Addition ${ref} : obligation en cours`;
}

export default function CaisseRestaurant() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dismissedFocusTableIdRef = useRef(null);
  const commercantId = resolveCommercantId(searchParams);
  const posClock = usePosDisplayTimeZone();
  const [screen, setScreen] = useState('plan');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [moduleOk, setModuleOk] = useState(null);
  const [moduleKitchenOk, setModuleKitchenOk] = useState(false);
  const [moduleResaOk, setModuleResaOk] = useState(false);
  const [resaOverlay, setResaOverlay] = useState({ byTableId: {}, reservations: [] });
  const [dayReservations, setDayReservations] = useState([]);
  const [sessionOk, setSessionOk] = useState(false);
  const [floors, setFloors] = useState([]);
  const [activeFloorId, setActiveFloorId] = useState(null);
  const [occupancy, setOccupancy] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [serviceDetail, setServiceDetail] = useState(null);
  const [additions, setAdditions] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [openCovers, setOpenCovers] = useState(2);
  const [openReservationId, setOpenReservationId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [splitAddition, setSplitAddition] = useState(null);
  const [tableOp, setTableOp] = useState(null);
  const [tableGroup, setTableGroup] = useState(null);
  const [transferDestId, setTransferDestId] = useState('');
  const [attachPickIds, setAttachPickIds] = useState([]);
  const [mergePeerId, setMergePeerId] = useState('');
  const [configFloorName, setConfigFloorName] = useState('');
  const [configTableLabel, setConfigTableLabel] = useState('');
  const [configTableCapacity, setConfigTableCapacity] = useState(4);
  const [configTableShape, setConfigTableShape] = useState('ROUND');
  const [configSelectedTableId, setConfigSelectedTableId] = useState(null);
  const [closePreview, setClosePreview] = useState({ canClose: null, blockers: [] });
  const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false);
  const [operationalHealth, setOperationalHealth] = useState(null);
  const [operationalAlertsDismissed, setOperationalAlertsDismissed] = useState(false);

  const canConfigureFloorPlan = useMemo(
    () => canPosAction(getAdminUserFromStorage(), 'caisse:restaurant:admin'),
    []
  );

  const showToast = (msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 4000);
  };

  const formatResaTime = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '—';
    }
  };

  const confirmCheckIn = async (reservationId, tableId) => {
    setProcessing(true);
    try {
      const key = createCaisseIdempotencyKey(`resa-checkin-${reservationId}`);
      await apiService.checkInRestaurantResa(
        reservationId,
        { tableId, openIdempotencyKey: key },
        { idempotencyKey: key, commercantId }
      );
      showToast('Client accueilli — service ouvert');
      setSelectedTable(null);
      await refreshAll(true);
    } catch (e) {
      showToast(e.message || 'Accueil impossible');
    } finally {
      setProcessing(false);
    }
  };

  const loadMeta = useCallback(async () => {
    if (!commercantId) {
      setError('Aucun commerçant sélectionné.');
      setModuleOk(false);
      return;
    }
    try {
      const com = await apiService.getCommercant(commercantId);
      const enabled = Boolean(com?.modulePosRestaurant) && Boolean(com?.moduleCaisse);
      setModuleOk(enabled);
      setModuleKitchenOk(
        Boolean(com?.modulePosRestaurantKitchen && com?.modulePosRestaurant && com?.moduleCaisse)
      );
      setModuleResaOk(
        Boolean(com?.modulePosRestaurantResa && com?.modulePosRestaurant && com?.moduleCaisse)
      );
      if (!com?.moduleCaisse) {
        setError('Activez le module Caisse (POS) pour utiliser le restaurant.');
      } else if (!com?.modulePosRestaurant) {
        setError(
          'Le module Restaurant POS n\'est pas activé pour ce commerçant (Super Admin → Modules).'
        );
      } else {
        setError('');
      }
      const sess = await apiService.getCaisseSession(commercantId);
      setSessionOk(Boolean(sess?.session?.id));
    } catch (e) {
      setModuleOk(false);
      setError(e.message || 'Impossible de charger le commerçant');
    }
  }, [commercantId]);

  const loadFloors = useCallback(async () => {
    const data = await apiService.getRestaurantFloors(commercantId, { includeInactive: screen === 'config' });
    const list = data?.floors || [];
    setFloors(list);
    if (!activeFloorId && list.length) setActiveFloorId(list[0].id);
  }, [commercantId, screen, activeFloorId]);

  const loadOccupancy = useCallback(async () => {
    const data = await apiService.getRestaurantOccupancy(commercantId, {
      includeInactiveTables: screen === 'config',
    });
    setOccupancy(data?.occupancy || []);
  }, [commercantId, screen]);

  const refreshAll = useCallback(
    async (silent = false) => {
      if (!commercantId || moduleOk === false) return;
      if (!silent) setRefreshing(true);
      try {
        await loadFloors();
        await loadOccupancy();
        try {
          const health = await apiService.getCaisseOperationalHealth(commercantId);
          setOperationalHealth(health);
        } catch {
          /* ignore */
        }
        if (moduleResaOk) {
          try {
            const overlay = await apiService.getRestaurantResaPlanOverlay(commercantId);
            setResaOverlay({
              byTableId: overlay?.byTableId || {},
              reservations: overlay?.reservations || [],
            });
            setDayReservations(overlay?.reservations || []);
          } catch {
            setResaOverlay({ byTableId: {}, reservations: [] });
            setDayReservations([]);
          }
        }
      } catch (e) {
        setError(e.message || 'Erreur de synchronisation');
      } finally {
        if (!silent) setRefreshing(false);
      }
    },
    [commercantId, moduleOk, moduleResaOk, loadFloors, loadOccupancy]
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadMeta();
      setLoading(false);
    })();
  }, [loadMeta]);

  useEffect(() => {
    if (moduleOk !== true) return;
    refreshAll(true);
    const t = window.setInterval(() => refreshAll(true), POLL_MS);
    return () => window.clearInterval(t);
  }, [moduleOk, refreshAll]);

  useEffect(() => {
    if (!canConfigureFloorPlan && screen === 'config') {
      setScreen('plan');
    }
  }, [canConfigureFloorPlan, screen]);

  useEffect(() => {
    if (searchParams.get('config') === 'prep' && canConfigureFloorPlan) {
      setScreen('config');
    }
  }, [searchParams, canConfigureFloorPlan]);

  const focusTableId = searchParams.get('focusTableId');
  const location = useLocation();
  const [viewAdditionId, setViewAdditionId] = useState(null);

  /** Entrée plan général (accueil / post-cuisine) : aucun panneau table auto. */
  useEffect(() => {
    if (!location.state?.restaurantPlanOnly) return;
    setSelectedTable(null);
    setTableOp(null);
    setViewAdditionId(null);
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null }
    );
  }, [
    location.state?.restaurantPlanOnly,
    location.pathname,
    location.search,
    navigate,
  ]);

  const clearFocusTableIdFromUrl = useCallback(() => {
    if (!searchParams.get('focusTableId')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('focusTableId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const closeServicePanel = useCallback(() => {
    setSelectedTable(null);
    setTableOp(null);
    setViewAdditionId(null);
    const fid = searchParams.get('focusTableId');
    if (fid) {
      dismissedFocusTableIdRef.current = fid;
      clearFocusTableIdFromUrl();
    }
  }, [searchParams, clearFocusTableIdFromUrl]);

  const requestCloseServicePanel = useCallback(() => {
    if (processing) return;
    closeServicePanel();
  }, [processing, closeServicePanel]);

  const servicePanelOpen = useMemo(
    () =>
      screen === 'plan' &&
      Boolean(selectedTable) &&
      selectedTable.operationalStatus === 'OCCUPIED',
    [screen, selectedTable]
  );

  useEffect(() => {
    if (!servicePanelOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || processing) return;
      if (viewAdditionId || splitAddition || releaseConfirmOpen) return;
      if (tableOp) {
        e.preventDefault();
        setTableOp(null);
        return;
      }
      e.preventDefault();
      closeServicePanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    servicePanelOpen,
    processing,
    closeServicePanel,
    viewAdditionId,
    splitAddition,
    releaseConfirmOpen,
    tableOp,
  ]);

  useEffect(() => {
    if (!focusTableId) return;
    if (
      dismissedFocusTableIdRef.current &&
      dismissedFocusTableIdRef.current !== focusTableId
    ) {
      dismissedFocusTableIdRef.current = null;
    }
  }, [focusTableId]);

  useEffect(() => {
    if (!focusTableId || !occupancy.length) return;
    if (dismissedFocusTableIdRef.current === focusTableId) return;
    if (selectedTable?.table?.id === focusTableId) return;
    const row = occupancy.find((r) => r.table?.id === focusTableId);
    if (!row) return;
    if (row.operationalStatus === 'OCCUPIED') {
      dismissedFocusTableIdRef.current = null;
      setSelectedTable(row);
      return;
    }
    dismissedFocusTableIdRef.current = focusTableId;
    clearFocusTableIdFromUrl();
  }, [focusTableId, occupancy, selectedTable?.table?.id, clearFocusTableIdFromUrl]);

  const reloadOpenServicePanel = useCallback(async (silent = false) => {
    const sid = selectedTable?.openSession?.id;
    if (!sid || !commercantId) return;
    try {
      const svc = await apiService.getRestaurantService(sid, commercantId);
      setServiceDetail(svc?.session || null);
      const adds = await apiService.listRestaurantServiceAdditions(sid, commercantId);
      setAdditions(adds?.additions || []);
      const grp = await apiService.getRestaurantServiceTableGroup(sid, commercantId);
      setTableGroup(grp?.group || null);
      const preview = await apiService.getRestaurantServiceClosePreview(sid, commercantId);
      setClosePreview({
        canClose: Boolean(preview?.canClose),
        blockers: preview?.blockers || [],
      });
    } catch (e) {
      if (!silent) showToast(e.message || 'Service introuvable');
    }
  }, [selectedTable?.openSession?.id, commercantId]);

  useEffect(() => {
    if (!selectedTable?.openSession?.id) {
      setServiceDetail(null);
      setAdditions([]);
      setClosePreview({ canClose: null, blockers: [] });
      return;
    }
    void reloadOpenServicePanel(false);
    const t = window.setInterval(() => reloadOpenServicePanel(true), POLL_MS);
    return () => window.clearInterval(t);
  }, [selectedTable?.openSession?.id, reloadOpenServicePanel]);

  const floorTables = useMemo(() => {
    return occupancy.filter((row) => row.table?.floorId === activeFloorId || !activeFloorId);
  }, [occupancy, activeFloorId]);

  const freeTablesOnFloor = useMemo(() => {
    return floorTables.filter((r) => r.operationalStatus === 'FREE');
  }, [floorTables]);

  const otherOccupiedSessions = useMemo(() => {
    const sid = selectedTable?.openSession?.id;
    if (!sid) return [];
    return occupancy.filter(
      (r) =>
        r.operationalStatus === 'OCCUPIED' &&
        r.openSession?.id &&
        r.openSession.id !== sid &&
        r.assignmentRole !== 'ATTACHED'
    );
  }, [occupancy, selectedTable]);

  const openServiceModal = async (row) => {
    if (screen === 'config') return;
    if (row.table?.id && dismissedFocusTableIdRef.current === row.table.id) {
      dismissedFocusTableIdRef.current = null;
    }
    setSelectedTable(row);
    if (row.operationalStatus === 'OCCUPIED') return;
    if (moduleResaOk && dayReservations.length) {
      const tableId = row.table?.id;
      setReservations(
        dayReservations.filter(
          (r) =>
            !r.posServiceSessions?.length &&
            ['CONFIRME', 'EN_ATTENTE'].includes(r.statut) &&
            (!r.posTableId || r.posTableId === tableId)
        )
      );
    } else {
      try {
        const resa = await apiService.getTableReservations(commercantId);
        setReservations((resa?.reservations || resa || []).slice(0, 50));
      } catch {
        setReservations([]);
      }
    }
    const defaultCovers = row.table?.capacity != null ? Number(row.table.capacity) : 2;
    setOpenCovers(Math.max(1, defaultCovers || 2));
    setOpenReservationId('');
  };

  const confirmOpenService = async () => {
    if (!selectedTable?.table?.id) return;
    setProcessing(true);
    try {
      const key = createCaisseIdempotencyKey('rest-open');
      const res = await apiService.openRestaurantService(
        {
          commercantId,
          tableId: selectedTable.table.id,
          coverCount: Number(openCovers) || 2,
          openIdempotencyKey: key,
          reservationTableId: openReservationId || null,
        },
        { idempotencyKey: key }
      );
      showToast(res.created === false ? 'Service déjà ouvert' : 'Service ouvert');
      const svc = res?.session;
      if (svc?.id) {
        setSelectedTable({
          ...selectedTable,
          operationalStatus: 'OCCUPIED',
          openSession: {
            id: svc.id,
            coverCount: svc.coverCount ?? (Number(openCovers) || 2),
            openedAt: svc.openedAt,
            additionsCount: svc.additionsCount ?? selectedTable.openSession?.additionsCount ?? 0,
          },
        });
      }
      await refreshAll(true);
    } catch (e) {
      if (e?.code === 'TABLE_SERVICE_ALREADY_OPEN' || e?.status === 409) {
        showToast('Table déjà occupée sur un autre terminal — actualisation');
        await refreshAll(true);
      } else {
        showToast(e.message || 'Ouverture impossible');
      }
    } finally {
      setProcessing(false);
    }
  };

  const runTableOp = async (fn) => {
    setProcessing(true);
    try {
      await fn();
      await refreshAll(true);
      if (selectedTable?.openSession?.id) {
        const grp = await apiService.getRestaurantServiceTableGroup(
          selectedTable.openSession.id,
          commercantId
        );
        setTableGroup(grp?.group || null);
      }
      setTableOp(null);
      setTransferDestId('');
      setAttachPickIds([]);
      setMergePeerId('');
    } catch (e) {
      if (e?.code === 'TABLE_ALREADY_ASSIGNED' || e?.status === 409) {
        showToast('Conflit : actualisez le plan de salle');
        await refreshAll(true);
      } else {
        showToast(e.message || 'Opération impossible');
      }
    } finally {
      setProcessing(false);
    }
  };

  const confirmTransfer = () =>
    runTableOp(async () => {
      const sid = selectedTable?.openSession?.id;
      if (!sid || !transferDestId) return;
      const key = createCaisseIdempotencyKey('rest-transfer');
      await apiService.transferRestaurantService(
        sid,
        { destinationTableId: transferDestId, idempotencyKey: key },
        { idempotencyKey: key, commercantId }
      );
      showToast('Table déplacée');
    });

  const confirmAttach = () =>
    runTableOp(async () => {
      const sid = selectedTable?.openSession?.id;
      if (!sid || !attachPickIds.length) return;
      const key = createCaisseIdempotencyKey('rest-attach');
      await apiService.attachRestaurantTables(
        sid,
        { tableIds: attachPickIds, idempotencyKey: key },
        { idempotencyKey: key, commercantId }
      );
      showToast('Tables rattachées');
    });

  const confirmDetach = (tableId) =>
    runTableOp(async () => {
      const sid = selectedTable?.openSession?.id;
      if (!sid) return;
      const key = createCaisseIdempotencyKey('rest-detach');
      await apiService.detachRestaurantTable(
        sid,
        { tableId, idempotencyKey: key },
        { idempotencyKey: key, commercantId }
      );
      showToast('Table retirée du groupe');
    });

  const confirmMerge = () =>
    runTableOp(async () => {
      const sid = selectedTable?.openSession?.id;
      if (!sid || !mergePeerId) return;
      const key = createCaisseIdempotencyKey('rest-merge');
      await apiService.mergeRestaurantOperational(
        sid,
        { peerSessionIds: [mergePeerId], idempotencyKey: key },
        { idempotencyKey: key, commercantId }
      );
      showToast('Services regroupés (additions séparées conservées)');
    });

  const executeCloseService = async ({ confirmLibere = false } = {}) => {
    const sid = selectedTable?.openSession?.id || serviceDetail?.id;
    if (!sid) return;
    setProcessing(true);
    try {
      await apiService.closeRestaurantService(sid, commercantId);
      showToast(confirmLibere ? 'Table libérée' : 'Service clôturé');
      setReleaseConfirmOpen(false);
      setSelectedTable(null);
      closeServicePanel();
      await refreshAll(true);
    } catch (e) {
      const blockers = e?.details?.blockers || e?.blockers;
      if (e?.code === 'SERVICE_CLOSE_BLOCKED' || e?.status === 409) {
        if (blockers?.length) {
          setClosePreview({ canClose: false, blockers });
        }
        showToast(
          blockers?.length
            ? 'Libération impossible : réglez les additions listées ci-dessous'
            : e.message || 'Fermeture refusée'
        );
      } else {
        showToast(e.message || 'Fermeture impossible');
      }
    } finally {
      setProcessing(false);
    }
  };

  const closeService = async (opts = {}) => {
    if (opts.confirmLibere) {
      setReleaseConfirmOpen(true);
      return;
    }
    await executeCloseService(opts);
  };

  const sendAdditionToKitchen = async (addition) => {
    if (!addition?.id || !moduleKitchenOk) return;
    if (addition.isCancelled || addition.statut === 'ANNULEE') {
      showToast('Addition annulée — envoi cuisine impossible', 'error');
      return;
    }
    setProcessing(true);
    try {
      const key = createCaisseIdempotencyKey(`rest-prep-${addition.id}`);
      const outcome = await apiService.sendKitchenCommandePrep(addition.id, {
        idempotencyKey: key,
        commercantId,
        sendMode: 'ALL_REMAINING',
      });
      const msg = formatPrepSendSuccessToast(outcome);
      if (msg) {
        showToast(msg);
      } else {
        showToast('Aucune ligne envoyée — vérifiez la réponse serveur', 'error');
      }
    } catch (e) {
      if (e?.code === 'PREP_NOTHING_TO_SEND' || e?.code === 'PREP_COMMANDE_CANCELLED') {
        showToast(formatPrepNothingToSendToast(e) || e.message, 'error');
      } else {
        showToast(e.message || 'Envoi cuisine impossible', 'error');
      }
    } finally {
      setProcessing(false);
    }
  };

  const activeAdditions = useMemo(
    () => additions.filter((a) => !(a.isCancelled || a.statut === 'ANNULEE')),
    [additions]
  );
  const historicalAdditions = useMemo(
    () => additions.filter((a) => a.isCancelled || a.statut === 'ANNULEE'),
    [additions]
  );

  const goOrder = (opts = {}) => {
    const sid = selectedTable?.openSession?.id || serviceDetail?.id;
    if (!sid) return;
    if (opts.ticketId) {
      const target = additions.find((a) => a.id === opts.ticketId);
      if (target && (target.isCancelled || target.statut === 'ANNULEE')) {
        showToast('Addition annulée — reprise impossible', 'error');
        return;
      }
    }
    const params = new URLSearchParams({
      commercantId,
      posServiceSessionId: sid,
      tableLabel: selectedTable?.table?.label || '',
      returnTo: 'restaurant',
    });
    if (selectedTable?.table?.id) params.set('posTableId', selectedTable.table.id);
    if (opts.newAddition) params.set('newAddition', '1');
    if (opts.ticketId) params.set('ticketId', opts.ticketId);
    navigate(`/caisse?${params.toString()}`);
  };

  const createFloor = async () => {
    const name = configFloorName.trim();
    if (!name) return;
    setProcessing(true);
    try {
      await apiService.createRestaurantFloor({ commercantId, name, sortOrder: floors.length });
      setConfigFloorName('');
      await loadFloors();
      showToast('Salle créée');
    } catch (e) {
      showToast(e.message || 'Erreur création salle');
    } finally {
      setProcessing(false);
    }
  };

  const createTable = async () => {
    if (!activeFloorId) return;
    const label = configTableLabel.trim();
    if (!label) return;
    const capacity = Number(configTableCapacity) || 4;
    const shape = normalizePlanShape(configTableShape);
    const dims = suggestedDimensionsForCapacity(capacity, shape);
    setProcessing(true);
    try {
      await apiService.createRestaurantTable({
        commercantId,
        floorId: activeFloorId,
        label,
        capacity,
        planShape: shape,
        planWidth: dims.width,
        planHeight: dims.height,
        planX: 40 + Math.random() * 200,
        planY: 40 + Math.random() * 200,
      });
      setConfigTableLabel('');
      await refreshAll(true);
      showToast('Table créée');
    } catch (e) {
      showToast(e.message || 'Erreur création table');
    } finally {
      setProcessing(false);
    }
  };

  const patchLocalTable = useCallback((tableId, patch) => {
    setOccupancy((prev) =>
      prev.map((row) =>
        row.table.id === tableId ? { ...row, table: { ...row.table, ...patch } } : row
      )
    );
  }, []);

  const persistTableLayout = useCallback(
    async (tableId, patch) => {
      try {
        await apiService.patchRestaurantTable(tableId, { commercantId, ...patch });
      } catch {
        showToast('Sauvegarde plan échouée');
        await refreshAll(true);
      }
    },
    [commercantId, refreshAll]
  );

  const saveConfigTableProps = async (patch) => {
    if (!configSelectedTableId) return;
    setProcessing(true);
    try {
      await apiService.patchRestaurantTable(configSelectedTableId, { commercantId, ...patch });
      patchLocalTable(configSelectedTableId, patch);
      showToast('Table mise à jour');
    } catch (e) {
      showToast(e.message || 'Mise à jour impossible');
    } finally {
      setProcessing(false);
    }
  };

  const confirmRetireTable = () => {
    if (!configSelectedRow?.table?.id) return;
    const label = configSelectedRow.table.label;
    const tableId = configSelectedRow.table.id;
    if (
      !window.confirm(
        `Retirer la table « ${label} » du plan de service ?\n\nLes tables avec historique seront archivées ; les tables vierges seront supprimées.`
      )
    ) {
      return;
    }
    setProcessing(true);
    (async () => {
      try {
        const res = await apiService.deleteRestaurantTable(tableId, commercantId);
        showToast(
          res?.mode === 'DELETED'
            ? `Table ${label} supprimée`
            : `Table ${label} archivée (historique conservé)`
        );
        setConfigSelectedTableId(null);
        await refreshAll(true);
      } catch (e) {
        showToast(e.message || 'Retrait impossible');
      } finally {
        setProcessing(false);
      }
    })();
  };

  const configSelectedRow = useMemo(
    () => occupancy.find((r) => r.table.id === configSelectedTableId) || null,
    [occupancy, configSelectedTableId]
  );

  if (loading) {
    return (
      <div className="p-6 text-slate-600" data-testid="restaurant-loading">
        Chargement du plan de salle…
      </div>
    );
  }

  if (moduleOk === false) {
    return (
      <div className="p-6 max-w-lg" data-testid="restaurant-module-blocked">
        <p className="text-red-700 font-medium">{error || 'Accès restaurant refusé'}</p>
        <Link
          to={buildCaisseAccueilHref(commercantId)}
          className="text-emerald-700 underline mt-4 inline-block"
        >
          Retour caisse
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="caisse-restaurant-root">
      <PosCaisseSharedHeader commercant={posClock.commercant} />
      <div className="px-4 pt-2">
        <PosOperationalAlertsBanner
          health={operationalHealth}
          commercantId={commercantId}
          dismissed={operationalAlertsDismissed}
          onDismiss={() => setOperationalAlertsDismissed(true)}
        />
      </div>
      <header className={`sticky top-0 ${POS_Z_CLASS.pageToolbar} bg-white border-b border-slate-200 px-4 py-3 flex flex-wrap items-center gap-3`}>
        <Link
          to={buildCaisseAccueilHref(commercantId)}
          className="inline-flex items-center gap-2 text-slate-700 min-h-[44px] px-2"
          data-testid="restaurant-back-caisse"
        >
          <ArrowLeft className="w-5 h-5" aria-hidden />
          Menu caisse
        </Link>
        <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <UtensilsCrossed className="w-5 h-5 text-emerald-600" aria-hidden />
          Plan de salle
        </h1>
        {!sessionOk && (
          <span className="text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1 text-sm flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            Ouvrez la caisse pour commander et encaisser
          </span>
        )}
        <div className="ml-auto flex gap-2">
          {moduleKitchenOk && (
            <Link
              to={`/caisse/kitchen?commercantId=${encodeURIComponent(commercantId)}`}
              data-testid="restaurant-open-kitchen"
              className="min-h-[44px] px-3 rounded-xl border border-slate-200 bg-slate-900 text-white text-sm font-medium inline-flex items-center gap-2"
            >
              <ChefHat className="w-4 h-4" />
              Cuisine
            </Link>
          )}
          <button
            type="button"
            data-testid="restaurant-refresh"
            className="min-h-[44px] min-w-[44px] rounded-xl border border-slate-200 bg-white px-3"
            onClick={() => refreshAll(false)}
            disabled={refreshing}
            aria-label="Actualiser"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            data-testid="restaurant-tab-plan"
            className={`min-h-[44px] px-4 rounded-xl border ${screen === 'plan' ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white border-slate-200'}`}
            onClick={() => setScreen('plan')}
          >
            <LayoutGrid className="w-5 h-5 inline mr-1" aria-hidden />
            Service
          </button>
          {canConfigureFloorPlan && (
            <button
              type="button"
              data-testid="restaurant-tab-config"
              className={`min-h-[44px] px-4 rounded-xl border ${screen === 'config' ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white border-slate-200'}`}
              onClick={() => setScreen('config')}
            >
              <Settings2 className="w-5 h-5 inline mr-1" aria-hidden />
              Configuration
            </button>
          )}
        </div>
      </header>

      {toast && (
        <div className="mx-4 mt-3 rounded-xl bg-slate-900 text-white px-4 py-3 text-sm" role="status">
          {toast}
        </div>
      )}

      <div className="px-4 pt-4 flex flex-wrap gap-2">
        {floors.map((f) => (
          <button
            key={f.id}
            type="button"
            data-testid={`restaurant-floor-${f.id}`}
            className={`min-h-[44px] px-4 rounded-full border ${activeFloorId === f.id ? 'bg-slate-900 text-white' : 'bg-white border-slate-200'}`}
            onClick={() => setActiveFloorId(f.id)}
          >
            {f.name}
          </button>
        ))}
      </div>

      {screen === 'config' && canConfigureFloorPlan && (
        <p className="text-sm text-slate-600 text-center max-w-7xl mx-auto px-4 -mt-1 mb-1">
          Plan de référence — déplacez et redimensionnez les tables ci-dessous.
        </p>
      )}

      <section className="p-4 max-w-7xl mx-auto w-full">
        <RestaurantFloorPlanCanvas
          mode={screen === 'config' && canConfigureFloorPlan ? 'config' : 'plan'}
          rows={floorTables}
          selectedTableId={configSelectedTableId}
          onSelectConfigTable={(row) => setConfigSelectedTableId(row.table.id)}
          onOpenServiceTable={openServiceModal}
          onLocalTablePatch={patchLocalTable}
          onPersistTableLayout={persistTableLayout}
          resaByTableId={resaOverlay.byTableId}
          moduleResaOk={moduleResaOk}
          formatResaTime={formatResaTime}
          formatElapsed={formatElapsed}
        />
      </section>

      {screen === 'config' && canConfigureFloorPlan && (
        <section className="p-4 max-w-7xl mx-auto w-full space-y-4" data-testid="restaurant-config">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Outils de configuration</h2>
            <p className="text-sm text-slate-600 mt-1">
              Salles, tables et propriétés — sans ouvrir de service depuis cet écran.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3">
            <h3 className="font-semibold text-slate-900">Nouvelle salle</h3>
            <input
              className="w-full border rounded-xl px-3 min-h-[44px]"
              placeholder="Nom de la salle"
              value={configFloorName}
              onChange={(e) => setConfigFloorName(e.target.value)}
              data-testid="restaurant-config-floor-name"
            />
            <button
              type="button"
              className="w-full min-h-[44px] rounded-xl bg-emerald-600 text-white font-medium"
              onClick={createFloor}
              disabled={processing}
            >
              Créer la salle
            </button>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3">
            <h3 className="font-semibold text-slate-900">Nouvelle table</h3>
            <input
              className="w-full border rounded-xl px-3 min-h-[44px]"
              placeholder="Numéro / nom (ex. T4, VIP…)"
              value={configTableLabel}
              onChange={(e) => setConfigTableLabel(e.target.value)}
              data-testid="restaurant-config-table-label"
            />
            <label className="block text-sm text-slate-600">
              Capacité habituelle
              <input
                type="number"
                min={1}
                className="w-full border rounded-xl px-3 min-h-[44px] mt-1"
                value={configTableCapacity}
                onChange={(e) => setConfigTableCapacity(e.target.value)}
              />
            </label>
            <label className="block text-sm text-slate-600">
              Forme
              <select
                className="w-full border rounded-xl px-3 min-h-[44px] mt-1"
                value={configTableShape}
                onChange={(e) => setConfigTableShape(e.target.value)}
                data-testid="restaurant-config-table-shape"
              >
                <option value="ROUND">Ronde</option>
                <option value="SQUARE">Carrée</option>
                <option value="RECTANGLE">Rectangulaire</option>
              </select>
            </label>
            <button
              type="button"
              className="w-full min-h-[44px] rounded-xl bg-emerald-600 text-white font-medium"
              onClick={createTable}
              disabled={processing || !activeFloorId}
              data-testid="restaurant-config-create-table"
            >
              Créer la table
            </button>
            <p className="text-sm text-slate-500">
              Déplacez et redimensionnez les tables sur le plan. Aucun service ne s&apos;ouvre en configuration.
            </p>
          </div>
          {configSelectedRow && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3" data-testid="restaurant-config-selected-table">
              <h3 className="font-semibold text-slate-900">Table sélectionnée — {configSelectedRow.table.label}</h3>
              <label className="block text-sm">
                Nom affiché
                <input
                  className="w-full border rounded-xl px-3 min-h-[44px] mt-1"
                  defaultValue={configSelectedRow.table.label}
                  key={`label-${configSelectedRow.table.id}-${configSelectedRow.table.label}`}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== configSelectedRow.table.label) saveConfigTableProps({ label: v });
                  }}
                />
              </label>
              <label className="block text-sm">
                Capacité habituelle
                <input
                  type="number"
                  min={1}
                  className="w-full border rounded-xl px-3 min-h-[44px] mt-1"
                  defaultValue={configSelectedRow.table.capacity ?? 4}
                  key={`cap-${configSelectedRow.table.id}`}
                  onBlur={(e) => {
                    const v = Number(e.target.value);
                    if (v >= 1 && v !== configSelectedRow.table.capacity) saveConfigTableProps({ capacity: v });
                  }}
                />
              </label>
              <label className="block text-sm">
                Forme
                <select
                  className="w-full border rounded-xl px-3 min-h-[44px] mt-1"
                  value={normalizePlanShape(configSelectedRow.table.planShape)}
                  onChange={(e) => saveConfigTableProps({ planShape: e.target.value })}
                >
                  <option value="ROUND">Ronde</option>
                  <option value="SQUARE">Carrée</option>
                  <option value="RECTANGLE">Rectangulaire</option>
                </select>
              </label>
              {normalizePlanShape(configSelectedRow.table.planShape) === 'RECTANGLE' && (
                <button
                  type="button"
                  className="w-full min-h-[44px] rounded-xl border border-slate-200"
                  onClick={() =>
                    saveConfigTableProps({
                      planRotation: configSelectedRow.table.planRotation === 90 ? 0 : 90,
                    })
                  }
                >
                  Pivoter 90° ({configSelectedRow.table.planRotation === 90 ? 'vertical' : 'horizontal'})
                </button>
              )}
              <div className="flex gap-2">
                {['SMALL', 'MEDIUM', 'LARGE'].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className="flex-1 min-h-[40px] rounded-lg border text-xs font-medium"
                    onClick={() => {
                      const dims = applySizePreset(
                        configSelectedRow.table.capacity,
                        configSelectedRow.table.planShape,
                        preset
                      );
                      saveConfigTableProps({ planWidth: dims.width, planHeight: dims.height });
                    }}
                  >
                    {preset === 'SMALL' ? 'Petite' : preset === 'MEDIUM' ? 'Moyenne' : 'Grande'}
                  </button>
                ))}
              </div>
              {configSelectedRow.table.actif === false && (
                <button
                  type="button"
                  className="w-full min-h-[44px] rounded-xl border border-emerald-300 text-emerald-900"
                  disabled={processing}
                  onClick={() => saveConfigTableProps({ actif: true })}
                  data-testid="restaurant-config-reactivate-table"
                >
                  Réactiver sur le plan
                </button>
              )}
              <button
                type="button"
                className="w-full min-h-[44px] rounded-xl border border-red-200 text-red-800 font-medium"
                disabled={processing || configSelectedRow.operationalStatus === 'OCCUPIED'}
                onClick={confirmRetireTable}
                data-testid="restaurant-config-retire-table"
              >
                Retirer la table du plan
              </button>
            </div>
          )}
          </div>
          {moduleKitchenOk && <RestaurantPrepConfigPanel commercantId={commercantId} />}
        </section>
      )}

      {moduleResaOk && screen === 'plan' && dayReservations.length > 0 && (
        <section className="px-4 pt-2" data-testid="restaurant-resa-day-panel">
          <div className="bg-white rounded-2xl border p-4 max-w-3xl mx-auto">
            <h2 className="font-semibold text-slate-900 mb-2">Réservations du jour</h2>
            <ul className="space-y-2 max-h-48 overflow-y-auto">
              {dayReservations.map((r) => {
                const tableLabel = r.posTable?.label;
                const arrived = Boolean(r.posRestaurantResaMeta?.arrivedAt || r.posServiceSessions?.length);
                return (
                  <li
                    key={r.id}
                    className="flex flex-wrap items-center gap-2 text-sm border rounded-xl px-3 py-2"
                    data-testid={`restaurant-resa-row-${r.id}`}
                  >
                    <span className="font-medium">{formatResaTime(r.heureArrivee)}</span>
                    <span>{r.nomReservation || r.telephone || 'Client'}</span>
                    <span className="text-slate-500">{r.nombrePersonnes} pers.</span>
                    {tableLabel && (
                      <span className="text-orange-700">{formatRestaurantTableOptionLabel(tableLabel)}</span>
                    )}
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100">{r.statut}</span>
                    {!arrived && r.posTableId && ['CONFIRME', 'EN_ATTENTE'].includes(r.statut) && (
                      <button
                        type="button"
                        className="ml-auto min-h-[36px] px-3 rounded-lg bg-emerald-600 text-white text-xs font-medium"
                        data-testid={`restaurant-resa-checkin-${r.id}`}
                        disabled={processing}
                        onClick={() => confirmCheckIn(r.id, r.posTableId)}
                      >
                        Accueillir
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      {screen === 'plan' && selectedTable && selectedTable.operationalStatus !== 'OCCUPIED' && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center p-4 z-30"
          role="presentation"
          onClick={() => !processing && closeServicePanel()}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4"
            data-testid="restaurant-open-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold">Ouvrir table {selectedTable.table.label}</h2>
            <label className="block text-sm">
              Couverts
              <input
                type="number"
                min={1}
                className="w-full border rounded-xl px-3 min-h-[44px] mt-1"
                value={openCovers}
                onChange={(e) => setOpenCovers(e.target.value)}
                data-testid="restaurant-open-covers"
              />
            </label>
            {reservations.length > 0 && (
              <label className="block text-sm">
                Réservation (facultatif)
                <select
                  className="w-full border rounded-xl px-3 min-h-[44px] mt-1"
                  value={openReservationId}
                  onChange={(e) => setOpenReservationId(e.target.value)}
                  data-testid="restaurant-open-reservation"
                >
                  <option value="">— Aucune —</option>
                  {reservations.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.nomReservation || r.telephone || r.id} — {r.nombrePersonnes} pers.
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                className="flex-1 min-h-[44px] rounded-xl border"
                data-testid="restaurant-open-modal-cancel"
                onClick={() => !processing && closeServicePanel()}
              >
                Annuler
              </button>
              <button
                type="button"
                className="flex-1 min-h-[44px] rounded-xl bg-emerald-600 text-white font-medium"
                onClick={confirmOpenService}
                disabled={processing || !sessionOk}
                data-testid="restaurant-confirm-open"
              >
                Ouvrir le service
              </button>
            </div>
          </div>
        </div>
      )}

      {screen === 'plan' && selectedTable && selectedTable.operationalStatus === 'OCCUPIED' && (
        <div
          className={`fixed inset-0 ${POS_Z_CLASS.serviceOverlay} flex items-end sm:items-center justify-center bg-black/40 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))]`}
          role="presentation"
          data-testid="restaurant-service-panel-backdrop"
          onClick={requestCloseServicePanel}
        >
          <div
            className="flex w-full max-w-lg max-h-[min(90dvh,calc(100dvh-2rem))] flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
            data-testid="restaurant-service-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="restaurant-service-panel-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 border-b px-5 py-4">
              <h2 id="restaurant-service-panel-title" className="text-lg font-semibold">
                {formatRestaurantTablePanelTitle(selectedTable.table.label)}
              </h2>
              <p className="text-sm text-slate-600 flex items-center gap-2 mt-1">
                <Clock className="w-4 h-4 shrink-0" />
                Ouvert depuis {formatElapsed(serviceDetail?.openedAt || selectedTable.openSession?.openedAt)}
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">
            {tableGroup?.assignments?.length > 1 && (
              <div className="text-sm bg-slate-50 border rounded-xl p-3" data-testid="restaurant-table-group">
                <p className="font-medium mb-1">Tables du service</p>
                <ul className="flex flex-wrap gap-2">
                  {tableGroup.assignments.map((a) => (
                    <li key={a.id} className="px-2 py-1 rounded-lg border bg-white text-xs">
                      {a.table?.label} ({a.role === 'PRIMARY' ? 'principale' : 'jointe'})
                      {a.role === 'ATTACHED' && (
                        <button
                          type="button"
                          className="ml-2 underline text-red-800"
                          data-testid={`restaurant-detach-${a.table?.label}`}
                          onClick={() => confirmDetach(a.tableId)}
                          disabled={processing}
                        >
                          Retirer
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className="min-h-[44px] rounded-xl border border-slate-200 text-sm font-medium"
                data-testid="restaurant-action-move"
                onClick={() => setTableOp('transfer')}
                disabled={processing}
              >
                Déplacer
              </button>
              <button
                type="button"
                className="min-h-[44px] rounded-xl border border-slate-200 text-sm font-medium"
                data-testid="restaurant-action-group"
                onClick={() => setTableOp('attach')}
                disabled={processing}
              >
                Regrouper tables
              </button>
              <button
                type="button"
                className="min-h-[44px] rounded-xl border border-slate-200 text-sm font-medium col-span-2"
                data-testid="restaurant-action-merge-services"
                onClick={() => setTableOp('merge')}
                disabled={processing || otherOccupiedSessions.length === 0}
              >
                Regrouper avec un autre service (sans fusion financière)
              </button>
            </div>
            {tableOp === 'transfer' && (
              <div className="border rounded-xl p-3 space-y-2" data-testid="restaurant-transfer-panel">
                <p className="text-sm font-medium">Table de destination (libre)</p>
                <select
                  className="w-full border rounded-xl min-h-[44px] px-3"
                  value={transferDestId}
                  onChange={(e) => setTransferDestId(e.target.value)}
                >
                  <option value="">— Choisir —</option>
                  {freeTablesOnFloor.map((r) => (
                    <option key={r.table.id} value={r.table.id}>
                      {formatRestaurantTableOptionLabel(r.table.label)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="w-full min-h-[44px] rounded-xl bg-slate-900 text-white"
                  data-testid="restaurant-confirm-transfer"
                  disabled={!transferDestId || processing}
                  onClick={confirmTransfer}
                >
                  Confirmer le déplacement
                </button>
              </div>
            )}
            {tableOp === 'attach' && (
              <div className="border rounded-xl p-3 space-y-2" data-testid="restaurant-attach-panel">
                <p className="text-sm font-medium">Tables libres à rattacher</p>
                <div className="flex flex-wrap gap-2">
                  {freeTablesOnFloor.map((r) => {
                    const checked = attachPickIds.includes(r.table.id);
                    return (
                      <label
                        key={r.table.id}
                        className={`min-h-[44px] px-3 flex items-center rounded-xl border cursor-pointer ${checked ? 'bg-emerald-50 border-emerald-400' : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="mr-2 w-5 h-5"
                          checked={checked}
                          onChange={() =>
                            setAttachPickIds((prev) =>
                              checked
                                ? prev.filter((id) => id !== r.table.id)
                                : [...prev, r.table.id]
                            )
                          }
                        />
                        {r.table.label}
                      </label>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="w-full min-h-[44px] rounded-xl bg-slate-900 text-white"
                  data-testid="restaurant-confirm-attach"
                  disabled={!attachPickIds.length || processing}
                  onClick={confirmAttach}
                >
                  Rattacher au service
                </button>
              </div>
            )}
            {tableOp === 'merge' && (
              <div className="border rounded-xl p-3 space-y-2" data-testid="restaurant-merge-panel">
                <p className="text-sm text-slate-600">
                  Les additions et paiements restent sur leur service d&apos;origine.
                </p>
                <select
                  className="w-full border rounded-xl min-h-[44px] px-3"
                  value={mergePeerId}
                  onChange={(e) => setMergePeerId(e.target.value)}
                >
                  <option value="">— Service voisin —</option>
                  {otherOccupiedSessions.map((r) => (
                    <option key={r.openSession.id} value={r.openSession.id}>
                      {formatRestaurantTableOptionLabel(r.table.label)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="w-full min-h-[44px] rounded-xl bg-slate-900 text-white"
                  data-testid="restaurant-confirm-merge"
                  disabled={!mergePeerId || processing}
                  onClick={confirmMerge}
                >
                  Regrouper opérationnellement
                </button>
              </div>
            )}
            <div>
              <h3 className="font-medium mb-2">Additions actives ({activeAdditions.length})</h3>
              <ul className="space-y-2" data-testid="restaurant-additions-list">
                {activeAdditions.map((a) => (
                  <AdditionRow
                    key={a.id}
                    a={a}
                    sessionOk={sessionOk}
                    moduleKitchenOk={moduleKitchenOk}
                    processing={processing}
                    onView={setViewAdditionId}
                    onResume={(id) => goOrder({ ticketId: id })}
                    onSendKitchen={sendAdditionToKitchen}
                    onSplit={setSplitAddition}
                  />
                ))}
                {activeAdditions.length === 0 && (
                  <li className="text-sm text-slate-500">Aucune addition active — créez-en une.</li>
                )}
              </ul>
              {historicalAdditions.length > 0 && (
                <>
                  <h3 className="font-medium mb-2 mt-4 text-slate-600">
                    Historique ({historicalAdditions.length})
                  </h3>
                  <ul className="space-y-2" data-testid="restaurant-additions-history">
                    {historicalAdditions.map((a) => (
                      <AdditionRow
                        key={a.id}
                        a={a}
                        sessionOk={sessionOk}
                        moduleKitchenOk={moduleKitchenOk}
                        processing={processing}
                        onView={setViewAdditionId}
                        onResume={(id) => goOrder({ ticketId: id })}
                        onSendKitchen={sendAdditionToKitchen}
                        onSplit={setSplitAddition}
                      />
                    ))}
                  </ul>
                </>
              )}
            </div>
            {closePreview.canClose === false && closePreview.blockers.length > 0 && (
              <div
                className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm space-y-2"
                data-testid="restaurant-close-blockers"
              >
                <p className="font-medium text-amber-950">
                  Libération impossible — réglez d&apos;abord :
                </p>
                <ul className="space-y-2">
                  {closePreview.blockers.map((b) => (
                    <li key={b.commandeId} className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <span className="text-amber-900">{describeCloseBlocker(b)}</span>
                      <button
                        type="button"
                        className="min-h-[40px] shrink-0 rounded-lg border border-amber-300 bg-white px-3 text-xs font-medium"
                        data-testid={`restaurant-blocker-open-${b.commandeId}`}
                        onClick={() => setViewAdditionId(b.commandeId)}
                      >
                        Voir
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            </div>
            <div className="shrink-0 space-y-2 border-t bg-white px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <button
              type="button"
              className="w-full min-h-[48px] rounded-xl bg-emerald-600 text-white font-medium flex items-center justify-center gap-2"
              onClick={() => goOrder({ newAddition: true })}
              disabled={!sessionOk}
              data-testid="restaurant-new-addition"
            >
              <Plus className="w-5 h-5" />
              Nouvelle addition
            </button>
            {closePreview.canClose === true && (
              <button
                type="button"
                className="w-full min-h-[48px] rounded-xl bg-slate-900 text-white font-medium"
                onClick={() => closeService({ confirmLibere: true })}
                disabled={processing}
                data-testid="restaurant-liberate-table"
              >
                Libérer la table
              </button>
            )}
            {closePreview.canClose === false && closePreview.blockers.length > 0 && (
              <p className="text-xs text-center text-slate-500 px-2">
                Encaissez ou annulez les additions listées pour libérer la table.
              </p>
            )}
            <button
              type="button"
              className="w-full min-h-[44px] rounded-xl border"
              data-testid="restaurant-service-panel-close"
              onClick={requestCloseServicePanel}
            >
              Fermer
            </button>
            </div>
          </div>
        </div>
      )}

      <RestaurantReleaseTableModal
        open={releaseConfirmOpen}
        onClose={() => setReleaseConfirmOpen(false)}
        onConfirm={() => executeCloseService({ confirmLibere: true })}
        processing={processing}
        tableLabel={selectedTable?.table?.label}
        floorName={
          floors.find((f) => f.id === selectedTable?.table?.floorId)?.name ||
          selectedTable?.table?.floor?.name
        }
        additions={additions}
        canClose={closePreview.canClose}
      />

      <RestaurantAdditionDetailModal
        open={Boolean(viewAdditionId)}
        additionId={viewAdditionId}
        commercantId={commercantId}
        showPrepTracking={moduleKitchenOk}
        onPrepSent={() => refreshAll(true)}
        onClose={() => setViewAdditionId(null)}
        onResumeAddition={
          viewAdditionId
            ? () => {
                const id = viewAdditionId;
                setViewAdditionId(null);
                goOrder({ ticketId: id });
              }
            : undefined
        }
      />

      <RestaurantSplitModal
        open={Boolean(splitAddition)}
        addition={splitAddition}
        commercantId={commercantId}
        serviceId={selectedTable?.openSession?.id || serviceDetail?.id}
        onClose={() => setSplitAddition(null)}
        onSuccess={async () => {
          showToast('Addition partagée');
          setSplitAddition(null);
          await refreshAll(true);
        }}
      />
    </div>
  );
}
